import type { TakeoverRequest, TakeoverResponse } from '@/composables/Device/DeviceTakeover'
import { resolveDeclineReason } from './takeover'

/**
 * How a device-group assignment came about (IDMETA-6).
 *
 * Every assignment path in the app (consented takeover, consented handover,
 * forced takeover, plain "assign me" from the dock map, the admin modal,
 * onboarding, GroupTabs) posts to the same backend endpoint with the same
 * body. The backend writes an audit row for each call so telemetry's
 * `metadata_user_id` can be reconciled later, but it cannot tell those paths
 * apart on its own. This `audit` block is how the client tells it.
 *
 * Nothing here is trusted for the *outcome*: the backend derives the previous
 * owner from its own write and records what the client claimed alongside it.
 * Everything here is descriptive, and a missing block is recorded as
 * `unspecified` rather than rejected.
 *
 * Pure module so it can be unit tested without stores or MQTT.
 */

/** `takeover` / `handover` are consented; `forced` bypassed the owner. */
export type AssignmentType = 'direct' | 'takeover' | 'handover' | 'forced'

/**
 * Why a takeover was forced.
 *
 * - `owner_offline`: presence said the current owner is gone, so no request was sent.
 * - `no_response`: the requester's own fallback timer expired with no reply at all.
 * - `auto_declined`: the owner's client answered on their behalf when the modal countdown ran out.
 * - `declined`: the owner explicitly pressed Decline.
 * - `declined_in_flight`: the owner's client declined because the aircraft was flying.
 */
export type ForceReason =
  | 'owner_offline'
  | 'no_response'
  | 'auto_declined'
  | 'declined'
  | 'declined_in_flight'

/** Which piece of UI issued the assignment. */
export type AssignmentSource =
  | 'takeover_flow'
  | 'dock_map'
  | 'admin_modal'
  | 'onboarding'
  | 'group_tabs'

export interface AssignmentAuditMeta {
  assignmentType: AssignmentType
  source: AssignmentSource
  /** For `forced`: which flow the force fell out of. */
  requestType?: 'takeover' | 'handover'
  reason?: ForceReason
  /** The MQTT takeover/handover request this assignment settles, when there was one. */
  requestId?: string
  /** Who the client believed owned the group. Recorded, never trusted. */
  previousUserID?: string
}

export interface AssignmentAuditPayload extends AssignmentAuditMeta {
  /** ISO-8601 time on the client when the request was built. */
  clientTimestamp: string
}

/** Final shape sent as `audit` in the assign-device-group body. */
export const buildAssignmentAudit = (
  meta: AssignmentAuditMeta,
  now: number = Date.now(),
): AssignmentAuditPayload => ({
  ...meta,
  clientTimestamp: new Date(now).toISOString(),
})

const requestTypeOf = (request: Pick<TakeoverRequest, 'type'>): 'takeover' | 'handover' =>
  request.type === 'Handover' ? 'handover' : 'takeover'

/** A plain assignment with nobody to ask: the group was free, or the caller already owned it. */
export const directAssignmentMeta = (source: AssignmentSource): AssignmentAuditMeta => ({
  assignmentType: 'direct',
  source,
})

/** The current owner (or handover recipient) pressed Allow. */
export const acceptedAssignmentMeta = (
  request: Pick<TakeoverRequest, 'type' | 'requestId'>,
  previousUserID?: string,
): AssignmentAuditMeta => ({
  assignmentType: requestTypeOf(request),
  source: 'takeover_flow',
  requestId: request.requestId,
  previousUserID,
})

/** The requester pressed Force Takeover. `reason` says what led there. */
export const forcedAssignmentMeta = (
  request: Pick<TakeoverRequest, 'type' | 'requestId'>,
  previousUserID: string | undefined,
  reason: ForceReason,
): AssignmentAuditMeta => ({
  assignmentType: 'forced',
  source: 'takeover_flow',
  requestType: requestTypeOf(request),
  reason,
  requestId: request.requestId,
  previousUserID,
})

/**
 * Maps a decline response to the reason a subsequent force would carry.
 * Older clients send only `isFlying`; `resolveDeclineReason` handles that.
 */
export const forceReasonFromDecline = (
  response: Pick<TakeoverResponse, 'reason' | 'isFlying'>,
): ForceReason => {
  switch (resolveDeclineReason(response)) {
    case 'no_response':
      return 'auto_declined'
    case 'flying':
      return 'declined_in_flight'
    default:
      return 'declined'
  }
}
